/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import Account, {AccountFields} from "../../../models/accountModel";
import {AccountAdminErrors, DataMode} from "../../../util/interfaces";
import {AdminContext} from "../../misc/adminContext";
import {EventType, navigate, ResponseData, showDialog, showSnackbar, validateEmail} from '@dvrd/dvr-controls';
import AccountDetails from "./accountDetails";
import {onAccountEvent} from "../../../util/events";
import {SettingsFields} from "../../../models/settingsModel";
import {ADMIN_USER_ROUTE} from "../../../util/constants";

const EMPTY_ERRORS: AccountAdminErrors = {
    zipCode: null,
    telNumber: null,
    companyName: null,
    address: null,
    city: null,
    coc: null,
    email: null,
    country: null,
};

export default function AccountDetailsController() {
    const context = useContext(AdminContext);
    const [account, setAccount] = useState<Account>(new Account({}));
    const [errors, setErrors] = useState<AccountAdminErrors>(EMPTY_ERRORS);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<DataMode>(DataMode.DETAILS);

    function onChange(key: keyof AccountFields & keyof SettingsFields, entity: 'account' | 'settings') {
        return function (value: string) {
            const _account = account.clone();
            if (entity === 'account')
                _account.setField(key, value);
            else
                _account.settings = _account.settings.setField(key, value);
            setAccount(_account);
        }
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        account.updateAdmin((account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                const user = context.activeUser;
                if (user)
                    context.onChangeContext({user: user.updateAccount(account)});
                onAccountEvent(account, EventType.UPDATE);
                showSnackbar('Het account is bijgewerkt');
                setMode(DataMode.DETAILS);
                setAccount(account);
            } else
                showDialog(data.message ?? 'Het bijwerken van het account is niet gelukt.', 'Bijwerken mislukt');
        })
    }

    function onClose() {
        context.onChangeContext({account: null});
        navigate(ADMIN_USER_ROUTE);
    }

    function onClickDelete() {
        showDialog('Weet je zeker dat je dit account wilt verwijderen?', 'Bevestig',
            ['Nee', {label: 'Ja', onClick: onConfirmDelete, primary: false}]);
    }

    function onConfirmDelete() {
        setLoading(true);
        account.deleteAdmin((account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar('Het account is verwijderd');
                onAccountEvent(account, EventType.DELETE);
                onClose();
            } else
                showDialog(data.message ?? 'Het verwijderen van het account is niet gelukt.', 'Verwijderen mislukt');
        });
    }

    function onResetError(key: keyof AccountAdminErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        }
    }

    function onChangeMode(mode: DataMode) {
        return function () {
            setMode(mode);
        }
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = Object.assign({}, errors);
        if (!account.companyName) {
            valid = false;
            _errors.companyName = 'De bedrijfsnaam mag niet leeg zijn';
        }
        if (!account.address) {
            valid = false;
            _errors.address = 'Het adres mag niet leeg zijn';
        }
        if (!account.zipCode) {
            valid = false;
            _errors.zipCode = 'De postcode mag niet leeg zijn';
        }
        if (!account.city) {
            valid = false;
            _errors.city = 'De stad mag niet leeg zijn';
        }
        if (!account.country) {
            valid = false;
            _errors.country = 'Het land mag niet leeg zijn';
        }
        if (!account.email) {
            valid = false;
            _errors.email = 'Het e-mailadres mag niet leeg zijn';
        } else if (!validateEmail(account.email)) {
            valid = false;
            _errors.email = 'Het e-mailadres is niet geldig';
        }
        if (!account.telNumber) {
            valid = false;
            _errors.telNumber = 'Het telefoonnummer mag niet leeg zijn';
        }
        if (!account.cocNumber) {
            valid = false;
            _errors.coc = 'Het KvK-nummer mag niet leeg zijn';
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        const account = context.activeAccount;
        if (account) setAccount(account.clone());
        else history.back();
    }, []);

    return (
        <AccountDetails onChange={onChange} onSubmit={onSubmit} onClose={onClose} onClickDelete={onClickDelete}
                        onResetError={onResetError} onChangeMode={onChangeMode} mode={mode} errors={errors}
                        account={account} loading={loading}/>
    )
}